import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import AssignmentIndRoundedIcon from "@mui/icons-material/AssignmentIndRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PersonRemoveRoundedIcon from "@mui/icons-material/PersonRemoveRounded";
import { selectUser } from "../../features/userSlice";
import { Card, PageHeader, PageShell, SectionHeader } from "../../components/UI/Page";
import { EmptyState, LoadingState, StatTile } from "../../components/UI/States";
import { BackLink, BranchSelect, Notice, SearchField } from "../../components/Fleet/FleetParts";
import { formatDate } from "../../components/Fleet/fleetFormat";
import { fetchTrainingVideo, removeTrainingRole, unassignTraining } from "./safetyApi";
import {
  Chip,
  ConfirmDialog,
  ProgressBar,
  matchesQuery,
  overdueText,
  pickBranch,
  plural,
  sameBranch,
  useUrlFilters,
} from "./SafetyParts";
import { SAFETY_PAGES } from "./safetyPages";
import AssignTrainingDialog from "./AssignTrainingDialog";
import { ReportActions, reportScope } from "./SafetyReport";

// One training video: who it's assigned to and how they're doing, plus
// assigning it to more people/roles, unassigning someone, and stopping a
// role from being auto-assigned. ?assign=1 opens the assign dialog (the
// upload dialog's "Assign it now" link).

const PAGE = SAFETY_PAGES.training;

const STATUSES = {
  all: { label: "Assigned", hint: "Everyone", tone: "default", test: () => true },
  todo: { label: "Not done", hint: "Still to watch", tone: "amber", test: (a) => !a.completed },
  overdue: { label: "Overdue", hint: "Past the due date", tone: "rose", test: (a) => a.overdue },
  done: { label: "Done", hint: "Finished", tone: "emerald", test: (a) => a.completed },
};

const sourceLabel = (source) =>
  source?.startsWith("role:") ? `${source.slice(5)} role` : "By name";

const StatusText = ({ a }) => {
  if (a.completed) {
    return (
      <span className="text-emerald-700 dark:text-emerald-300">
        Done {formatDate(a.completedAt)}
        <span className="app-muted block text-xs">
          {a.completionType === "quiz" ? "Passed the quiz" : "Marked complete"}
        </span>
      </span>
    );
  }
  if (a.overdue) return <span className="font-semibold text-rose-700 dark:text-rose-300">{overdueText(a.dueAt)}</span>;
  if (a.dueSoon) return <span className="font-semibold text-amber-700 dark:text-amber-300">Due soon</span>;
  return <span className="app-muted">Not done</span>;
};

const TrainingVideoDetail = () => {
  const { id } = useParams();
  const user = useSelector(selectUser);
  const [params, setParams] = useSearchParams();
  const filters = useUrlFilters(Object.keys(STATUSES), "all");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [assigning, setAssigning] = useState(params.get("assign") === "1");
  const [unassign, setUnassign] = useState(null);
  const [stopRole, setStopRole] = useState(null);

  const load = useCallback(() => {
    if (!user?.id) return;
    fetchTrainingVideo(user.id, id)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((e) => setError(e.message));
  }, [user?.id, id]);

  useEffect(() => load(), [load]);

  const closeAssign = () => {
    setAssigning(false);
    if (params.get("assign")) {
      const next = new URLSearchParams(params);
      next.delete("assign");
      setParams(next, { replace: true });
    }
  };

  const video = data?.video;
  const assignments = data?.assignments;
  const branches = useMemo(
    () => [...new Set((assignments || []).map((a) => a.branch))].sort((a, b) => a.localeCompare(b)),
    [assignments]
  );
  const inBranch = (assignments || []).filter((a) => !filters.branch || sameBranch(a.branch, filters.branch));
  const visible = inBranch
    .filter(STATUSES[filters.status].test)
    .filter((a) => matchesQuery(filters.query, a.name, a.email, a.branch, sourceLabel(a.source)))
    .sort((a, b) => {
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  const roleOf = (a) => (a?.source?.startsWith("role:") ? a.source.slice(5) : null);
  const ruleExists = (role) => video?.roleRules.some((r) => sameBranch(r.role, role));
  const doneCount = inBranch.filter((a) => a.completed).length;

  return (
    <PageShell size="xl">
      <AssignTrainingDialog
        open={assigning && Boolean(video)}
        video={video}
        userId={user?.id}
        onClose={closeAssign}
        onAssigned={load}
      />
      <ConfirmDialog
        open={Boolean(unassign)}
        title={`Unassign ${unassign?.name || "this person"}?`}
        confirmLabel="Unassign"
        danger
        onClose={() => setUnassign(null)}
        onConfirm={async () => {
          await unassignTraining(user.id, id, unassign.id);
          load();
        }}
      >
        <p>They won't see this video in their training list anymore.</p>
        {unassign?.completed && <p>Their completion record goes with it.</p>}
        {roleOf(unassign) && ruleExists(roleOf(unassign)) && (
          <p className="font-semibold text-amber-700 dark:text-amber-300">
            They were assigned through the {roleOf(unassign)} role, which is still auto-assigned — the mobile app will
            assign it to them again within a day. To keep them off it, also stop assigning it to {roleOf(unassign)}.
          </p>
        )}
      </ConfirmDialog>
      <ConfirmDialog
        open={Boolean(stopRole)}
        title={`Stop assigning to ${stopRole?.role}?`}
        confirmLabel="Stop assigning"
        onClose={() => setStopRole(null)}
        onConfirm={async () => {
          await removeTrainingRole(user.id, id, stopRole.role);
          load();
        }}
      >
        <p>
          New people who get the {stopRole?.role} role won't be assigned this video anymore. Everyone who already has it
          keeps it — unassign them below if they shouldn't.
        </p>
      </ConfirmDialog>

      <BackLink to={PAGE.path}>Training Videos</BackLink>
      {error && <Notice tone="rose">{error}</Notice>}
      {!data && !error && <LoadingState label="Loading video…" />}

      {video && (
        <>
          <PageHeader
            icon={PAGE.icon}
            tone={PAGE.tone}
            eyebrow="Training Video"
            title={video.title}
            description={
              video.description ||
              `Uploaded ${formatDate(video.createdAt)}${video.addedByName ? ` by ${video.addedByName}` : ""}.`
            }
            actions={
              <button type="button" className="app-btn app-btn-primary" onClick={() => setAssigning(true)}>
                <AssignmentIndRoundedIcon sx={{ fontSize: 18 }} /> Assign
              </button>
            }
          />

          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <SectionHeader
                title={`${doneCount} of ${inBranch.length} done`}
                description={
                  video.quizEnabled
                    ? "People finish by passing the quiz (70% or better) in the mobile app."
                    : "People finish by tapping Mark as Complete in the mobile app."
                }
              />
              <ProgressBar
                completed={doneCount}
                overdue={inBranch.filter((a) => a.overdue).length}
                total={inBranch.length}
              />
            </Card>
            <Card>
              <SectionHeader title="Auto-assigned roles" />
              {video.roleRules.length === 0 ? (
                <p className="app-muted text-sm">None — only the people picked by name have it.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-1.5">
                    {video.roleRules.map((r) => (
                      <span key={r.role} className="app-chip">
                        {r.role}
                        <button
                          type="button"
                          onClick={() => setStopRole(r)}
                          className="app-muted -mr-1 rounded-full hover:text-[color:var(--app-text)]"
                          aria-label={`Stop assigning to ${r.role}`}
                          title={`Stop assigning to ${r.role}`}
                        >
                          <CloseRoundedIcon sx={{ fontSize: 14 }} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <p className="app-muted mt-2 text-xs">Anyone who gets one of these roles later is assigned automatically.</p>
                </>
              )}
            </Card>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {Object.entries(STATUSES).map(([key, s]) => (
              <StatTile
                key={key}
                label={s.label}
                value={inBranch.filter(s.test).length}
                hint={s.hint}
                tone={s.tone}
                active={filters.status === key}
                onClick={() => filters.set("status", key)}
              />
            ))}
          </div>

          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <SearchField value={filters.query} onChange={(v) => filters.set("q", v)} placeholder="Search name, email, or role…" />
            <BranchSelect
              value={pickBranch(branches, filters.branch)}
              onChange={(v) => filters.set("branch", v)}
              branches={branches}
            />
            <span className="app-muted text-sm tabular-nums sm:ml-auto">{plural(visible.length, "person", "people")}</span>
            <ReportActions
              kind="training-video"
              userId={user?.id}
              tone={PAGE.tone}
              disabled={!visible.length}
              selection={{
                videoId: id,
                ids: visible.map((a) => a.id),
                branch: pickBranch(branches, filters.branch),
                scope: reportScope({
                  branch: pickBranch(branches, filters.branch),
                  status: filters.status === "all" ? null : STATUSES[filters.status].label,
                  query: filters.query,
                }),
              }}
            />
          </div>

          <div className="app-card overflow-hidden rounded-2xl border">
            {visible.length === 0 ? (
              <EmptyState
                compact
                icon={AssignmentIndRoundedIcon}
                title={assignments.length ? "Nobody matches these filters." : "Not assigned to anyone yet."}
                description={assignments.length ? undefined : "Click Assign to pick roles or people."}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="app-table">
                  <thead>
                    <tr>
                      <th className="!pl-4 sm:!pl-5">Person</th>
                      <th>Branch</th>
                      <th>Assigned through</th>
                      <th>Due</th>
                      <th>Status</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((a) => (
                      <tr key={a.id}>
                        <td className="!pl-4 sm:!pl-5">
                          <span className="block font-semibold">{a.name}</span>
                          <span className="app-muted block text-xs">{a.email}</span>
                        </td>
                        <td className="whitespace-nowrap">{a.branch}</td>
                        <td className="whitespace-nowrap">
                          <Chip tone={a.source?.startsWith("role:") ? "violet" : "slate"}>{sourceLabel(a.source)}</Chip>
                        </td>
                        <td className="whitespace-nowrap tabular-nums">{a.dueAt ? formatDate(a.dueAt) : "—"}</td>
                        <td className="whitespace-nowrap">
                          <StatusText a={a} />
                        </td>
                        <td className="text-right">
                          <button
                            type="button"
                            className="app-icon-btn"
                            onClick={() => setUnassign(a)}
                            aria-label={`Unassign ${a.name}`}
                            title="Unassign"
                          >
                            <PersonRemoveRoundedIcon fontSize="small" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <p className="app-muted mt-3 text-xs">
            To add or change a quiz, use Safety Manager → Training Videos in the Big C mobile app.
          </p>
        </>
      )}
    </PageShell>
  );
};

export default TrainingVideoDetail;
